import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router, useLocation } from '@fs/zion-router'
import { Usabilla } from '@fs/zion-usabilla'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { setAppLocales } from '@fs/zion-locale'
import { css, Global } from '@emotion/core'

import { DSCProvider } from './components/state/DSCStore'
import App from './DsApp'
import getIsTWAMode, { captureTwaMode } from './components/shared/helpers/twaMode'
import { captureActivityEntry } from './components/shared/hooks/useActivityReturnTo'
import useDSApplicationMode from './components/shared/hooks/useDSApplicationMode'
import { useCorrections } from './components/aam/AppAam/hooks/hooks'
import useAdmin from './useAdmin'
import { getUsabillaUrl } from './getUsabillaUrl'
import { shouldHideFeedbackWidget } from './globalChromeRoutes'
import useLayoutVisibility from './components/shared/hooks/useLayoutVisibility'
import ActivityCover, { hasActivityCover } from './components/shared/components/ActivityCover'
import ActivityExitProvider from './components/shared/components/ActivityExitProvider'
import * as serviceWorker from './serviceWorker'
import './locales'
import supportedLanguages from './supportedLanguages'

captureTwaMode()
// Must run before the router so the recorded history length predates any route the app pushes itself.
captureActivityEntry()

// For activities from TWA that have removed visual load flicker with a full-screen color cover, use
// the cover during Split flag resolution. The default full-screen spinner is kept in all other cases.
const flagOptions = hasActivityCover() ? { LoadingFallback: ActivityCover } : undefined

const usabillaCss = css`
  position: relative;
  z-index: 6;
`

const tapHighlightCss = css`
  html {
    -webkit-tap-highlight-color: transparent;
  }
`

const AdminRoot = React.lazy(() => import('@fs/zion-root').then((module) => ({ default: module.AdminRoot })))
const Footer = React.lazy(() => import('@fs/zion-footer'))

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        `cidVal.toLowerCase is not a function`, // Adobe https://familysearch.slack.com/archives/C0FPL3ZL7/p1638807591465400?thread_ts=1638806934.465200&cid=C0FPL3ZL7
        `ReferenceError: Can't find variable: IntersectionObserver`,
        `SecurityError: The operation is insecure.`,
        `UnknownError: Database deleted by request of the user`, // Safari mobile indexedDB split.io bug
        `Extension context invalidated.`,
        `UnhandledRejection: Non-Error promise rejection captured with value: EU compliance was not acknowledged by the user.`,
        `TypeError: Object.values(...).flatMap is not a function`,
        'Request failed with status code 401', // Happens when a user session times out
        'ResizeObserver loop limit exceeded', // Benign browser bug in Chrome / Edge on Windows
        'Network Error',
        'AbortError: The transaction was aborted, so the request cannot be fulfilled.',
        'Failed to fetch', // Generally Google Ad blockers
        // Browser page translation swaps React's text nodes for <font> elements. https://github.com/facebook/react/issues/11538
        `Failed to execute 'removeChild' on 'Node'`,
        `Failed to execute 'insertBefore' on 'Node'`,
        'NotFoundError: The object can not be found here.', // Safari wording of the two errors above
      ],
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  setAppLocales(supportedLanguages)

  return (
    <Router basename={base}>
      <DSCProvider>
        <AppRoot />
      </DSCProvider>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    {/* Use a minimal, invisible Suspense fallback so that
        lazy-loaded routes (like MyHeritage) don't flash
        the global NoticeLoading spinner over their own
        custom loading / transition UIs. */}
    <Suspense fallback={null}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities} flagOptions={flagOptions}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

const AppRoot = () => {
  const location = useLocation()
  const { effectiveShowHeader, effectiveShowFooter } = useLayoutVisibility()
  const {
    CurrentRoot = Root,
    rootProps = { header: { hidden: !effectiveShowHeader }, footer: { hidden: !effectiveShowFooter } },
    shouldRenderAppRoot = true,
  } = useAdmin({
    showHeader: effectiveShowHeader,
    showFooter: effectiveShowFooter,
    AdminRootComponent: AdminRoot,
    FooterComponent: Footer,
  })
  const mode = useDSApplicationMode()
  const isTWA = getIsTWAMode()
  const isCenterOrKiosk = mode !== 'WEB'
  const isCorrections = useCorrections()
  const showHelpChatbot = mode === 'WEB'
  const usabillaUrl = getUsabillaUrl(location?.pathname)
  const showFeedbackWidget = !isCenterOrKiosk && !isCorrections && !shouldHideFeedbackWidget(location?.pathname)

  // Don't render anything until we know whether or not to show the app root, to avoid rendering the app root and then immediately unmounting it if we determine we need to show the admin root.
  if (!shouldRenderAppRoot) {
    return null
  }

  return (
    <CurrentRoot {...rootProps} analytics helpChatbot={showHelpChatbot}>
      {isTWA && <Global styles={tapHighlightCss} />}
      {isTWA ? (
        <ActivityExitProvider>
          <App />
        </ActivityExitProvider>
      ) : (
        <App />
      )}
      {showFeedbackWidget && (
        <div css={usabillaCss}>
          <Usabilla key={usabillaUrl} url={usabillaUrl} mobileBreakpoint="xl" hideOnMobile />
        </div>
      )}
    </CurrentRoot>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// Drop the pre-React hold that public/index.html painted.
document.getElementById('activity-hold')?.remove()

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
